























































































import cloneDeep from 'lodash.clonedeep'
import Vue from 'vue'
import Component from 'vue-class-component'
import { Getter, Mutation, Action } from 'vuex-class'
import { TweenMax, Power1 } from 'gsap'
import { Watch, Prop } from 'vue-property-decorator'
import { IIntroQuestionState } from '@/store/modules/e-learnings/courses/types'
import { themeColors } from '@/plugins/vuetify/theme'
import { read } from '@/core/db/db'
import { Database } from '@/core/db/types'
import { ICourseData, IIntroQuestionData } from '../types' 
import { IQuestionData } from '@/components/questions/types'
import { sleep } from '@/core/utilities/time'
import { gradientBackgroundFromColor } from '@/utilities/colors'


@Component({
  components: { }
})
export default class IntroQuestionsPage extends Vue {

  @Prop() courseId!: string
  data: ICourseData | null = null

  page = 1  //* gets set by pagination and buttons
  index = 0 //* follows after saving / loading question / animation
  currentQuestion: { definition: IIntroQuestionData, state: IIntroQuestionState } | null = null

  @Getter('windowHeight', { namespace: 'system' }) windowHeight!: boolean

  // * loading indicator
  @Action('startLoading', { namespace: 'system' }) startLoading!: () => void
  @Action('stopLoading', { namespace: 'system' }) stopLoading!: () => void
  @Getter('loading', { namespace: 'system' }) loading!: boolean

  // * intro question state
  @Getter('introQuestion', { namespace: 'courses' }) introQuestion!: (params: {id: string, introQuestionId: string}) => IIntroQuestionState | null
  @Mutation('setIntroQuestion', { namespace: 'courses' }) setIntroQuestion!: (params: { id: string, introQuestionId: string, value: IIntroQuestionState }) => void
  @Mutation('setIntroQuestionScore', { namespace: 'courses' }) setIntroQuestionScore!: (params: { id: string, introQuestionId: string, value: number }) => void
  @Action('loadObject', { namespace: 'courses' }) loadCourseState!: (id: string) => Promise<boolean>
  @Action('saveObject', { namespace: 'courses' }) saveCourseState!: (id: string) => Promise<boolean>

  // * for intro question definitions
  @Getter('preferredVersion', { namespace: 'profiles' }) preferredVersion!: boolean
  @Getter('preferredDomain', { namespace: 'profiles' }) preferredDomain!: boolean
  @Mutation('setIntroQuestionsDone', { namespace: 'profiles' }) setIntroQuestionsDone!: (courseId: string) => void
  @Action('save', { namespace: 'profiles' }) saveUserProfile!: () => Promise<boolean>

  // * question helpers
  get questions() { 
    return !this.data ? [] : this.data.modules.flatMap(module => module.introQuestions)
  }

  get questionText() { return this.currentQuestion ? this.currentQuestion.definition.question : '' }  
  get labelLeft() { return this.currentQuestion ? this.currentQuestion.definition.labelLeft : '' }  
  get labelRight() { return this.currentQuestion ? this.currentQuestion.definition.labelRight : '' }     
  get inverted() { return this.currentQuestion ? this.currentQuestion.definition.invertedScale : false }  
  get score() { return this.currentQuestion ? this.currentQuestion.state.score || this.currentQuestion.definition.start : 1 }
  set score(score: number) { 
    if (this.currentQuestion) {
      this.currentQuestion.state.score = score
    }
  }

  // * other helpers
  get menuColor() { return themeColors.menu }
  get gradientBackground() { return gradientBackgroundFromColor(themeColors.menuBackground) }
  get smileyHeight() { 
    if (['xs'].includes(this.$vuetify.breakpoint.name)) return '100px'
    if (['sm'].includes(this.$vuetify.breakpoint.name)) return '160px'
    return '200px'
  }

  // * methods
  async mounted() {
    this.startLoading()
    await this.loadCourseState(this.courseId)
    await this.loadCourseData(this.courseId)
    this.onPageChanged(this.page, null)
    this.stopLoading()
  }


  async loadCourseData(courseId: string) {
    const path = `/content/${this.preferredDomain}-${this.preferredVersion}/courses/${courseId}`
    const response = await read(Database.FIRESTORE, { path })
    this.data = response.data! as ICourseData
  }


  @Watch('page')
  async onPageChanged(newPage: number, oldPage: number | null) {
    if (oldPage !== null) {
      await this.saveCurrentQuestion()
    }
    const animationDirection = oldPage && newPage < oldPage ? 1 : -1
    await this.fadeOut(animationDirection)
    const newIndex = newPage - 1      
    this.loadQuestion(newIndex)
    await this.fadeIn(animationDirection)
  }


  loadQuestion(index: number) {
    const definition = this.questions[index]
    const defaultState = {
      id: definition.id,
      score: definition.score || definition.start,
      par: definition.par,
      invertedScale: definition.invertedScale
    }
    const state = cloneDeep(this.introQuestion({id: this.courseId, introQuestionId: definition.id}) || defaultState)
    this.currentQuestion = { definition, state }

    // * trigger animation:
    this.onScoreChanged(this.currentQuestion.state.score || 50, this.currentQuestion.state.score || 50)
  }


  async saveCurrentQuestion() {
    this.setIntroQuestion({
      id: this.courseId,
      introQuestionId: this.currentQuestion!.state.id,
      value: this.currentQuestion!.state
    })
    await this.saveCourseState(this.courseId)
  }


  @Watch('score')
  onScoreChanged(newScore: number, oldScore: number) {
    const PAR_SCORE = 50
    const WIDTH = 6
    let MAX_HEIGHT = 0
    let TOP_CORRECTION = 0
    switch(this.$vuetify.breakpoint.name) {
      case 'xs':
        MAX_HEIGHT = 100
        TOP_CORRECTION = 10
        break
      case 'sm':
        MAX_HEIGHT = 160
        TOP_CORRECTION = 12
        break
      default:
        MAX_HEIGHT = 200
        TOP_CORRECTION = 20
    }      

    const height = Math.abs(this.score - PAR_SCORE) / PAR_SCORE * MAX_HEIGHT
    const mouthHappy: any = document.getElementById('mouth-happy')!
    const mouthSad: any = document.getElementById('mouth-sad')!

    const top = MAX_HEIGHT - height + WIDTH + TOP_CORRECTION

    if ( (newScore >= PAR_SCORE && !this.inverted)
      || (newScore <= PAR_SCORE && this.inverted)
    ) {
      // * Happy
      mouthHappy.style.display = 'block'
      mouthSad.style.display = 'none'
      TweenMax.to( '#mouth-happy', .5, { opacity: 1, height, top: top - TOP_CORRECTION, ease: Power1.easeOut } )
      TweenMax.to( '#mouth-sad', .5, { opacity: 0, height: 0, top: MAX_HEIGHT, ease: Power1.easeOut } )
    }
    else {
      // * Sad
      mouthSad.style.display = 'block'
      mouthHappy.style.display = 'none'
      TweenMax.to( '#mouth-sad', .5, { opacity: 1, height, top: top + TOP_CORRECTION, ease: Power1.easeOut } )
      TweenMax.to( '#mouth-happy', .5, { opacity: 0, height: 0, top: MAX_HEIGHT, ease: Power1.easeOut } )
    }
    
    TweenMax.to( '.left-eye', .5, { opacity: 1, ease: Power1.easeOut } )
    TweenMax.to( '.right-eye', .5, { opacity: 1, ease: Power1.easeOut } )
  }

  async saveAndClose() {
    await this.saveCurrentQuestion()
    this.setIntroQuestionsDone(this.courseId)
    await this.saveUserProfile()
    this.$router.push('/')
  }


  async fadeOut(direction: number) {
    const duration = .25
    TweenMax.to( '#question-text', duration, { autoAlpha: 0, x: 200 * direction, ease: Power1.easeIn } )
    TweenMax.to( '.labels', duration, { autoAlpha: 0, x: 20 * direction, ease: Power1.easeIn } )
    return sleep(duration * 1000)
  }


  async fadeIn(direction: number) {
    const duration = .25
    TweenMax.to( '#question-text', 0, { autoAlpha: 0, x: 200 * -direction,  ease: Power1.easeIn, delay: duration } )
    TweenMax.to( '#question-text', duration, { autoAlpha: 1, x: 0, ease: Power1.easeOut, delay: duration } )
    TweenMax.to( '.labels', 0, { autoAlpha: 0, x: 20 * -direction,  ease: Power1.easeIn, delay: duration } )
    TweenMax.to( '.labels', duration, { autoAlpha: 1, x: 0, ease: Power1.easeOut, delay: duration } )
    return sleep(duration * 1000)
  }
}
